import React from "react";

import { formatDate } from "~/utils/bloodOxygenSaturationFormatters";
import { HealthBreakdown } from "../Shared";

interface BloodOxygenSaturationData {
  date: string;
  low: number;
  high: number;
  avg: number;
}

interface HourlyBloodOxygenSaturationData {
  datetime: string;
  hour: number;
  low: number;
  high: number;
  avg: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BloodOxygenSaturationBreakdownProps {
  selectedRange: TimeRange;
  filteredData: (BloodOxygenSaturationData | HourlyBloodOxygenSaturationData)[];
  breakdownTitle: string;
  loading: boolean;
  error: string | null;
}

export const BloodOxygenSaturationBreakdown: React.FC<
  BloodOxygenSaturationBreakdownProps
> = ({ selectedRange, filteredData, breakdownTitle, loading, error }) => {
  const getKey = (item: BloodOxygenSaturationData | HourlyBloodOxygenSaturationData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item as HourlyBloodOxygenSaturationData).datetime
      : (item as BloodOxygenSaturationData).date;
  };

  const getTimeDisplay = (item: BloodOxygenSaturationData | HourlyBloodOxygenSaturationData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item as HourlyBloodOxygenSaturationData).hour + ":00"
      : formatDate((item as BloodOxygenSaturationData).date, selectedRange);
  };

  const getValues = (item: BloodOxygenSaturationData | HourlyBloodOxygenSaturationData) => [
    {
      label: "Low",
      value: item.low,
      color: "#3b82f6", // blue-500
      unit: "%",
    },
    {
      label: "Avg",
      value: item.avg,
      color: "#22c55e", // green-500
      unit: "%",
    },
    {
      label: "High",
      value: item.high,
      color: "#ef4444", // red-500
      unit: "%",
    },
  ];

  return (
    <HealthBreakdown
      selectedRange={selectedRange}
      filteredData={filteredData}
      breakdownTitle={breakdownTitle}
      loading={loading}
      error={error}
      dataTypeLabel="Blood Oxygen Saturation Data"
      getKey={getKey}
      getTimeDisplay={getTimeDisplay}
      getValues={getValues}
      noDataMessage="No blood oxygen saturation data available"
    />
  );
};